/**
 * Guest Guard (Functional)
 *
 * Prevents authenticated users from accessing routes meant for
 * unauthenticated visitors (e.g. the login page). Redirects
 * authenticated users to the dashboard.
 *
 * Uses a 1-second timeout to wait for MSAL initialisation on
 * page refresh before deciding.
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { Observable, timer } from 'rxjs';
import { filter, take } from 'rxjs/operators';

const GUEST_TIMEOUT_MS = 1000;

export const guestGuard: CanActivateFn = (_route, _state): Observable<boolean | UrlTree> => {
  const msalService = inject(MsalService);
  const msalBroadcast = inject(MsalBroadcastService);
  const router = inject(Router);

  const dashboardUrl: UrlTree = router.parseUrl('/');

  return new Observable<boolean | UrlTree>((observer) => {
    let resolved = false;

    // Wait for MSAL initialisation
    const initialized$ = msalBroadcast.inProgress$.pipe(
      filter((status) => status === 'none'),
      take(1),
    );

    const sub = initialized$.subscribe(() => {
      if (!resolved) {
        resolved = true;
        const account = msalService.instance.getActiveAccount();
        observer.next(account ? dashboardUrl : true);
        observer.complete();
      }
    });

    // After timeout, if still unresolved, allow access (guest)
    timer(GUEST_TIMEOUT_MS).subscribe(() => {
      if (!resolved) {
        resolved = true;
        sub.unsubscribe();
        const account = msalService.instance.getActiveAccount();
        observer.next(account ? dashboardUrl : true);
        observer.complete();
      }
    });

    return () => sub.unsubscribe();
  });
};